<template>
  <div class="pg-cat">
    <!-- 섹션 레벨: 제목 -->
    <div class="p-field">
      <label class="p-field__label">Section Title</label>
      <input
        v-model="sectionData.content.sectionTitle"
        type="text"
        class="p-input"
        placeholder="섹션 제목"
      />
    </div>

    <!-- 문항 탭 -->
    <question-tabs
      :count="questions.length"
      v-model="currentIndex"
      @add="addQuestion"
      @remove="removeQuestion"
    />

    <template v-if="current">
      <div class="p-field">
        <label class="p-field__label">Question / 지시문</label>
        <input
          v-model="current.questionText"
          type="text"
          class="p-input"
          placeholder="예: 단어 카드를 알맞은 카테고리로 분류하세요"
        />
      </div>

      <!-- 카테고리(컬럼) -->
      <div class="pg-cat__block">
        <div class="pg-cat__block-head">
          카테고리 (분류 컬럼)
          <span class="pg-cat__block-sub">예: 음료수 / 과일 / 음식</span>
        </div>
        <div class="pg-cat__chips">
          <div
            v-for="(cat, index) in current.categories"
            :key="cat.id"
            class="pg-cat__chip"
          >
            <span class="pg-cat__chip-idx">{{ index + 1 }}</span>
            <input
              v-model="cat.label"
              type="text"
              class="p-input pg-cat__chip-input"
              placeholder="카테고리명"
            />
            <button
              type="button"
              class="pg-cat__chip-x"
              title="제거"
              @click.prevent="removeCategory(index)"
            >
              <i class="material-icons-round">close</i>
            </button>
          </div>
        </div>
        <div class="pg-cat__actions">
          <ABtn kind="yellow" icon="add" @click.prevent="addCategory">카테고리 추가</ABtn>
        </div>
      </div>

      <!-- 단어 카드 -->
      <div class="pg-cat__block">
        <div class="pg-cat__block-head">
          단어 카드
          <span class="pg-cat__block-sub">각 카드를 정답 카테고리에 연결</span>
        </div>
        <div v-if="!current.categories.length" class="pg-cat__empty">
          먼저 카테고리를 추가하세요.
        </div>
        <div v-else class="pg-cat__cards">
          <div
            v-for="(card, index) in current.cards"
            :key="index"
            class="pg-cat__card"
          >
            <span class="pg-cat__card-idx">{{ index + 1 }}</span>
            <input
              v-model="card.text"
              type="text"
              class="p-input pg-cat__card-input"
              placeholder="단어 카드 텍스트"
            />
            <select v-model="card.categoryId" class="p-input pg-cat__card-select">
              <option value="">정답 카테고리 선택</option>
              <option
                v-for="cat in current.categories"
                :key="cat.id"
                :value="cat.id"
              >
                {{ cat.label || cat.id }}
              </option>
            </select>
            <button
              type="button"
              class="pg-cat__icon-btn pg-cat__icon-btn--danger"
              title="제거"
              @click.prevent="removeCard(index)"
            >
              <i class="material-icons-round">delete</i>
            </button>
          </div>
        </div>
        <div class="pg-cat__actions">
          <ABtn
            kind="ghost"
            icon="add"
            :disabled="!current.categories.length"
            @click.prevent="addCard"
          >단어 카드 추가</ABtn>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import ABtn from "@/components/Pineapple/ABtn.vue";
import QuestionTabs from "@/components/Practice/QuestionTabs.vue";
import { useQuestions } from "@/components/Practice/useQuestions";

const sectionData = defineModel({ type: Object, required: true });

const makeEmpty = () => ({
  questionText: "",
  categories: [],
  cards: []
});

const { questions, currentIndex, current, addQuestion, removeQuestion } = useQuestions(
  sectionData,
  makeEmpty,
  ["questionText", "categories", "cards"]
);

const addCategory = () => {
  current.value.categories.push({ id: `cat_${Date.now()}`, label: "" });
};

const removeCategory = (index) => {
  const removed = current.value.categories[index];
  current.value.categories.splice(index, 1);
  if (removed) {
    current.value.cards.forEach((card) => {
      if (card.categoryId === removed.id) card.categoryId = "";
    });
  }
};

const addCard = () => {
  current.value.cards.push({ text: "", categoryId: "" });
};

const removeCard = (index) => {
  current.value.cards.splice(index, 1);
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-cat {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__block {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 16px;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    background: $p-panel;
  }

  &__block-head {
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__block-sub {
    font-size: 11.5px;
    font-weight: 500;
    color: $p-ink-soft;
  }

  &__empty {
    font-size: 12.5px;
    color: $p-ink-soft;
    padding: 6px 2px;
  }

  // ── 카테고리 칩 ──
  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 6px 4px 8px;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
    background: $p-bg;
  }

  &__chip-idx {
    min-width: 20px;
    height: 20px;
    display: inline-grid;
    place-items: center;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 10px;
    font-weight: 800;
  }

  &__chip-input {
    max-width: 140px;
    height: 32px;
  }

  &__chip-x {
    width: 24px;
    height: 24px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;

    > i { font-size: 14px; }

    &:hover { background: $p-err-soft; color: $p-err; }
  }

  // ── 단어 카드 ──
  &__cards {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__card {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
    background: $p-bg;
  }

  &__card-idx {
    min-width: 24px;
    height: 24px;
    display: inline-grid;
    place-items: center;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    flex-shrink: 0;
  }

  &__card-input {
    flex: 1;
    min-width: 0;
  }

  &__card-select {
    max-width: 200px;
    flex-shrink: 0;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;
    flex-shrink: 0;

    > i { font-size: 16px; }

    &--danger {
      color: $p-err;
      &:hover { background: $p-err-soft; }
    }
  }

  &__actions {
    display: flex;
    gap: 8px;
  }
}
</style>
